import { Component, Input, OnInit, forwardRef, Inject } from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { getParsedValue, getStringValue, mentionConfig, clearPql } from './pql-editor.helper';

import { ICondition, IValue } from '@platform500services/p500-ui-kit';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { IDataService } from '../../../services/data/data-service.interface';
import { getComponent, getSignsDataPql } from '../../../helpers/preset-condition.helper';
import { ConditionTypes } from '../../../helpers/preset-condition.interface';
import { MentionConfig } from 'angular-mentions';

@Component({
  selector: 'gtd-pql-editor',
  templateUrl: './pql-editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PqlEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PqlEditorComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class PqlEditorComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() cssClass;
  @Input() message: string;
  @Input() conditions: ICondition[] = [];

  public form = new UntypedFormGroup({
    pqlString: new UntypedFormControl(),
    pqlParsed: new UntypedFormArray([]),
  });

  public presetHelpers = {
    getValueComponentByType: (formControl: UntypedFormControl, key: string, type: ConditionTypes = '', label: string = '') => {
      return getComponent(formControl, key, type, label, this.dataService.getData());
    },
    getSignsDataPql,
  };

  public mentionConfig: MentionConfig;
  private subscribers: any = {};
  private onChange: any = () => {};
  public onTouched: any = () => {};
  private pqlParsedValueChanges = (pqlParsedControlValue: IValue[]) => {
    const stringPqlValue = getStringValue(pqlParsedControlValue);
    this.pqlString.setValue(stringPqlValue, { emitEvent: false });
    this.onChange(clearPql(stringPqlValue));
    this.onTouched();
  };

  constructor(private fb: UntypedFormBuilder, @Inject('IDataService') private dataService: IDataService) {}

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: string): void {
    this.pqlString.setValue(value, { emitEvent: false });
    const parsedValues = getParsedValue(value);
    this.form.setControl('pqlParsed', this.fb.array(parsedValues));
    if (this.subscribers.pqlParsed) {
      this.subscribers.pqlParsed.unsubscribe();
    }
    this.subscribers.pqlParsed = this.pqlParsed.valueChanges.subscribe(this.pqlParsedValueChanges);
  }

  ngOnInit(): void {
    this.mentionConfig = mentionConfig(this.conditions.map((item) => item.key));
    this.subscribers.pqlString = this.pqlString.valueChanges
      .pipe(debounceTime(900), distinctUntilChanged())
      .subscribe((pqlStringControlValue: string) => {
        const parsedValues = getParsedValue(pqlStringControlValue);
        this.form.setControl('pqlParsed', this.fb.array(parsedValues));
        if (this.subscribers.pqlParsed) {
          this.subscribers.pqlParsed.unsubscribe();
        }
        this.subscribers.pqlParsed = this.pqlParsed.valueChanges.subscribe(this.pqlParsedValueChanges);

        this.onChange(clearPql(pqlStringControlValue));
        this.onTouched();
      });
  }

  get pqlString(): UntypedFormControl {
    return this.form.get('pqlString') as UntypedFormControl;
  }

  get pqlParsed(): UntypedFormArray {
    return this.form.get('pqlParsed') as UntypedFormArray;
  }

  addCondition(): void {
    this.pqlParsed.push(this.getCondition());
  }

  removeCondition(i: number): void {
    this.pqlParsed.removeAt(i);
  }

  getCondition(state: IValue = { key: null, config: 'AND', operator: null, value: null }): UntypedFormControl {
    return new UntypedFormControl(state);
  }

  validate(): ValidationErrors {
    return this.pqlParsed.invalid
      ? {
          invalidPql: 'Invalid PQL',
        }
      : null;
  }
}
